/**
 * Unified offer-product resolver for admin preview + storefront iframe.
 * 1) Storefront GraphQL (window.weupsellStorefrontOfferResolution)
 * 2) Server GET /api/public/product/random (fallback)
 */
import { buildRandomApiQueryParams } from "./build-random-api-params.js";
import {
  appendContextProductExclusionToRandomApiParams,
  normalizeShopifyProductId,
  resolveOfferContextProduct,
  shouldExcludeContextProductFromOffer,
} from "./products-to-offer-context.js";

function getStorefrontResolver() {
  if (typeof window === "undefined") return null;

  const local = window.weupsellStorefrontOfferResolution;
  if (local && typeof local.resolveStorefrontOfferProducts === "function") {
    return local;
  }

  try {
    const parent = window.parent?.weupsellStorefrontOfferResolution;
    if (parent && typeof parent.resolveStorefrontOfferProducts === "function") {
      return parent;
    }
  } catch (_) {
    // Cross-origin iframe — parent not readable.
  }

  return null;
}

function normalizeServerRows(result) {
  const rows = result?.data?.data ?? result?.data ?? [];
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => row?.productInfo).filter(Boolean);
}

/**
 * Resolve offer products using the new storefront module first, then server API.
 */
export async function resolveOfferProducts({
  productsToOffer,
  productCount = 1,
  allowMultipleProducts = false,
  contextProduct = null,
  preferCart = false,
  surface = "",
  apiGet,
  caller = "react-resolveOfferProducts",
  debugPopup = null,
}) {
  if (!productsToOffer?.type) return allowMultipleProducts ? [] : null;

  const requestedLimit = Math.max(1, Number(productCount) || 1);
  const ctx =
    contextProduct ??
    resolveOfferContextProduct(
      typeof window !== "undefined" ? window : null,
      { preferCart, surface }
    );
  const offerType = productsToOffer.type;

  const storefront = getStorefrontResolver();
  if (storefront) {
    try {
      const excludeForStorefront = shouldExcludeContextProductFromOffer(offerType)
        ? normalizeShopifyProductId(ctx?.id)
        : "";

      const storefrontProducts = await storefront.resolveStorefrontOfferProducts({
        offerType,
        productsToOffer,
        requestedLimit,
        contextProduct: ctx,
        excludeProductIds: excludeForStorefront || "",
      });

      if (storefrontProducts?.length) {
        return allowMultipleProducts
          ? storefrontProducts
          : storefrontProducts[0];
      }
    } catch (error) {
      console.warn(
        "[resolveOfferProducts] Storefront resolver failed, using server fallback:",
        error
      );
    }
  }

  if (typeof apiGet !== "function") {
    return allowMultipleProducts ? [] : null;
  }

  const shop = window?.Shopify?.shop || window.location.hostname;
  let params = buildRandomApiQueryParams(productsToOffer, {
    shop,
    productCount: requestedLimit,
    contextProduct: ctx,
  });

  params = appendContextProductExclusionToRandomApiParams(
    params,
    offerType,
    normalizeShopifyProductId(ctx?.id)
  );

  const activePopupTitle = debugPopup?.template_id?.title
    ? String(debugPopup.template_id.title).trim()
    : "";
  if (activePopupTitle) params.popupTitle = activePopupTitle;

  const activePopupId =
    debugPopup?._id != null ? debugPopup._id : debugPopup?.id;
  if (activePopupId != null && activePopupId !== "") {
    params.popupId = String(activePopupId);
  }
  params.caller = caller;

  const query = new URLSearchParams(
    Object.entries(params).reduce((acc, [key, val]) => {
      if (val != null && val !== "") acc[key] = String(val);
      return acc;
    }, {})
  ).toString();

  const result = await apiGet(`/api/public/product/random?${query}`);
  const products = normalizeServerRows(result);

  if (!products.length) return allowMultipleProducts ? [] : null;
  return allowMultipleProducts ? products : products[0];
}

/** @deprecated Use resolveOfferProducts */
export const resolveOfferProductsClient = resolveOfferProducts;
